<template>
  <div class="flex flex-col gap-3">
    <div v-if="loading" class="py-4">
      <TheLoadingComponent size="small" />
    </div>

    <!-- A known onboarding: its status, never the form again (a second
         registration would create a second legal unit for the same SIREN). -->
    <InfoPanel
      v-else-if="showStatus"
      :type="statusPanelType"
      :title="$t('global.peppol.onboarding_status')"
      :description="statusLabel"
    >
      <template v-if="statusHint || canOpenOnboarding || canRestart" #default>
        <span v-if="statusHint">{{ statusHint }}</span>
        <span v-if="canOpenOnboarding" class="mt-2 flex">
          <BaseButton
            :title="$t('button.peppol.open_onboarding')"
            icon="arrow-up-right-from-square"
            color="primary"
            size="small"
            @click="openOnboardingLink"
          />
        </span>
        <span v-if="canRestart" class="mt-2 flex">
          <BaseButton
            :title="$t('button.peppol.restart_onboarding')"
            icon="redo"
            color="primary"
            size="small"
            @click="restartOnboarding"
          />
        </span>
      </template>
      <template #cta>
        <BaseButton
          :title="$t('button.peppol.refresh_status')"
          icon="sync"
          is-outline
          size="small"
          @click="fetchStatus"
        />
      </template>
    </InfoPanel>

    <!-- Status unknown (the first read failed): no form either. -->
    <InfoPanel
      v-else-if="!status"
      type="danger"
      :description="$t('global.peppol.status_unavailable')"
    >
      <template #cta>
        <BaseButton
          :title="$t('button.peppol.refresh_status')"
          icon="sync"
          is-outline
          size="small"
          @click="fetchStatus"
        />
      </template>
    </InfoPanel>

    <template v-else>
      <InfoPanel
        v-if="failed"
        type="danger"
        :description="status.error || $t('global.peppol.status.failed_hint')"
      />
      <InfoPanel
        type="warning"
        :description="$t('global.peppol.onboarding_email')"
      />
      <template v-if="$can('manage_content')">
        <SelectComponent
          v-model="responsibleId"
          name="responsible_id"
          :label="$t('global.peppol.responsible')"
          :values="company.users ?? []"
          display-key="full_name"
          searchable
          edition-mode
        />
        <div>
          <BaseButton
            :title="$t('button.peppol.register')"
            icon="paper-plane"
            color="primary"
            :disabled="!responsibleId"
            @click="register"
          />
        </div>
      </template>
    </template>
  </div>
</template>

<script>
import { mapActions } from 'vuex'

import InfoPanel from '@c/baseComponents/InfoPanel.vue'
import SelectComponent from '@c/BaseFormComponent/SelectComponent.vue'
import TheLoadingComponent from '@c/TheLoadingComponent.vue'

/** How the back's onboarding_status values are shown (Crema's doc). */
const STATUS_GROUPS = {
  pending: 'registering',
  started: 'form',
  draft: 'form',
  pending_onboarding: 'form',
  onboarding_in_progress: 'form',
  pending_manual_review: 'review',
  pending_external_review: 'review',
  registration_pending: 'review',
  finalization_in_progress: 'review',
  active: 'active',
  expired: 'expired',
  failed: 'failed',
  rejected: 'failed',
  cancellation_requested: 'failed'
}

/**
 * BCSolutions registration of a company, after FM's component of the same
 * name. The status is read on open and on "refresh" only: the back refreshes
 * it every hour, no polling here.
 */
export default {
  name: 'PeppolRegisterBcSolutionsComponent',
  components: { InfoPanel, SelectComponent, TheLoadingComponent },
  props: {
    company: { type: Object, required: true }
  },
  data() {
    return { status: null, loading: false, responsibleId: null }
  },
  computed: {
    group() {
      const value = this.status?.onboarding_status?.toLowerCase()
      if (!value) return null
      if (value.startsWith('awaiting_')) return 'review'
      return STATUS_GROUPS[value] ?? 'registering'
    },
    failed() {
      return this.group === 'failed'
    },
    // A failed job without legal unit leaves the form open, to try again.
    showStatus() {
      if (!this.group) return false
      return !(this.failed && !this.status.registered)
    },
    statusLabel() {
      return this.$t(`global.peppol.status.${this.group}`)
    },
    statusHint() {
      if (this.group === 'form') {
        return this.status.onboarding_url
          ? this.$t('global.peppol.status.form_link_hint')
          : this.$t('global.peppol.status.form_email_hint')
      }
      if (this.failed) {
        return this.status.error || this.$t('global.peppol.status.failed_hint')
      }
      return null
    },
    statusPanelType() {
      if (this.group === 'active') return 'success'
      if (this.failed) return 'danger'
      if (this.group === 'expired') return 'warning'
      return 'info'
    },
    canOpenOnboarding() {
      return this.group === 'form' && !!this.status.onboarding_url
    },
    // Only an expired onboarding restarts (409 otherwise), whatever
    // available_actions says.
    canRestart() {
      return this.group === 'expired' && this.$can('manage_content')
    }
  },
  mounted() {
    this.fetchStatus()
  },
  methods: {
    ...mapActions({
      getStatus: 'company/getBcSolutionsPeppolStatus',
      registerToBcSolutions: 'company/registerCompanyToBcSolutions',
      restart: 'company/restartBcSolutionsOnboarding'
    }),
    // `loading` hides the buttons while a call runs: no double registration.
    run(promise) {
      this.loading = true
      return promise
        .then((status) => {
          this.status = status
        })
        .catch(() => {})
        .finally(() => {
          this.loading = false
        })
    },
    fetchStatus() {
      return this.run(this.getStatus({ id: this.company.id }))
    },
    register() {
      return this.run(
        this.registerToBcSolutions({
          id: this.company.id,
          responsible_id: this.responsibleId
        })
      )
    },
    restartOnboarding() {
      return this.run(this.restart({ id: this.company.id }))
    },
    openOnboardingLink() {
      window.open(this.status.onboarding_url, '_blank', 'noopener')
    }
  }
}
</script>
